"use client"

import { View, StyleSheet } from "react-native"
import { Text, Surface, DataTable, IconButton, useTheme } from "react-native-paper"
import { Eye } from "lucide-react"
import type { Project } from "../../types/project"

interface ProjectsSectionProps {
  projects: Project[]
  onView: (project: Project) => void
  title?: string
  emptyMessage?: string
}

export function ProjectsSection({ projects, onView, title, emptyMessage }: ProjectsSectionProps) {
  const theme = useTheme()

  return (
    <View style={styles.container}>
      {title && (
        <View style={styles.header}>
          <Text variant="titleLarge" style={[styles.title, { color: theme.colors.onSurface }]}>
            {title}
          </Text>
        </View>
      )}

      <Surface style={[styles.tableContainer, { backgroundColor: theme.colors.elevation.level1 }]} elevation={1}>
        <DataTable>
          <DataTable.Header>
            <DataTable.Title style={styles.headerCell}>Name</DataTable.Title>
            <DataTable.Title style={styles.headerCell}>Description</DataTable.Title>
            <DataTable.Title style={styles.headerCell}>Path</DataTable.Title>
            <DataTable.Title style={[styles.headerCell, { justifyContent: "center" }]}>Actions</DataTable.Title>
          </DataTable.Header>

          {projects.length === 0 ? (
            <DataTable.Row>
              <DataTable.Cell colSpan={4} style={styles.emptyCell}>
                <Text style={{ textAlign: "center", color: theme.colors.onSurfaceVariant }}>
                  {emptyMessage || "No items available"}
                </Text>
              </DataTable.Cell>
            </DataTable.Row>
          ) : (
            projects.map((project) => (
              <DataTable.Row key={project.id}>
                <DataTable.Cell style={styles.cell}>
                  {project.name}
                  {project.needsSync && (
                    <Text style={styles.syncWarning}> ⚠️ Data needs sync</Text>
                  )}
                </DataTable.Cell>
                <DataTable.Cell style={styles.cell}>{project.description || "No description"}</DataTable.Cell>
                <DataTable.Cell style={styles.cell}>{project.path}</DataTable.Cell>
                <DataTable.Cell style={styles.actionsCell}>
                  <IconButton
                    icon={() => <Eye size={18} color={theme.colors.primary} />}
                    size={20}
                    onPress={() => onView(project)}
                    aria-label="View project"
                  />
                </DataTable.Cell>
              </DataTable.Row>
            ))
          )}
        </DataTable>
      </Surface>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    marginTop: 16,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  title: {
    fontWeight: "500",
  },
  tableContainer: {
    borderRadius: 12,
    overflow: "hidden",
  },
  headerCell: {
    flex: 1,
    justifyContent: "center", // Center the header text
    alignItems: "center", // Center the header text
  },
  cell: {
    flex: 1,
    paddingRight: 8,
    // Add these properties for proper text wrapping
    whiteSpace: "normal",
    wordWrap: "break-word",
    maxWidth: "30vw", // Limit maximum width
  },
  actionsCell: {
    flex: 1,
    justifyContent: "center", // Center the action button
    alignItems: "center", // Center the action button
  },
  emptyCell: {
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 16,
  },
  syncWarning: {
    fontSize: 11,
    color: "#ff9800",
    fontStyle: "italic",
  },
})

